import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import { HOURS, hoursInfo, Reveal, MonoLabel } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Preventive Maintenance",
  "Brake Service & Repair",
  "Engine Diagnostics & Repair",
  "Suspension, Steering & Tires",
  "Electrical & Battery",
  "Something Else / Not Sure",
];

const EMPTY = {
  name: "",
  contact: "",
  vehicle: "",
  service: "",
  preferred_date: "",
  notes: "",
};

const inputCls =
  "w-full border border-line bg-void px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-400 outline-none transition-colors duration-200 focus:border-gold";

const labelCls =
  "mb-2 block font-mono text-xs uppercase tracking-[0.22em] text-zinc-300";

export default function BookingContact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const todayIdx = hoursInfo().idx;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Request received — we'll be in touch to confirm your appointment.");
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not send your request. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" className="relative py-24 lg:py-32">
      <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-12 px-5 sm:px-8 md:grid-cols-12 lg:gap-16 lg:px-14">
        <Reveal className="md:col-span-7">
          <MonoLabel>Service Request</MonoLabel>
          <h2 className="mt-4 font-display text-5xl uppercase leading-[0.9] tracking-wide sm:text-6xl">
            Book Your <span className="text-gold">Slot</span>
          </h2>
          <p className="mt-5 max-w-lg text-sm leading-relaxed text-zinc-300">
            Tell us what your vehicle needs. We confirm every appointment
            personally before you roll in.
          </p>

          <form
            data-testid="booking-request-form"
            onSubmit={submit}
            className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2"
          >
            <div>
              <label className={labelCls} htmlFor="bk-name">Name</label>
              <input
                id="bk-name"
                data-testid="booking-input-name"
                className={inputCls}
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                required
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-contact">Phone or Email</label>
              <input
                id="bk-contact"
                data-testid="booking-input-contact"
                className={inputCls}
                value={form.contact}
                onChange={set("contact")}
                placeholder="Best way to reach you"
                required
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-vehicle">Vehicle</label>
              <input
                id="bk-vehicle"
                data-testid="booking-input-vehicle"
                className={inputCls}
                value={form.vehicle}
                onChange={set("vehicle")}
                placeholder="e.g. 2014 Honda Accord"
                required
              />
            </div>
            <div>
              <label className={labelCls} htmlFor="bk-date">Preferred Date</label>
              <input
                id="bk-date"
                data-testid="booking-input-date"
                type="date"
                className={inputCls}
                value={form.preferred_date}
                onChange={set("preferred_date")}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="bk-service">Service Needed</label>
              <select
                id="bk-service"
                data-testid="booking-select-service"
                className={`${inputCls} appearance-none`}
                value={form.service}
                onChange={set("service")}
                required
              >
                <option value="" disabled>
                  Select a service…
                </option>
                {SERVICE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="bk-notes">Notes</label>
              <textarea
                id="bk-notes"
                data-testid="booking-input-notes"
                rows={4}
                className={`${inputCls} resize-none`}
                value={form.notes}
                onChange={set("notes")}
                placeholder="Anything we should know — noises, warning lights, history…"
              />
            </div>
            <button
              type="submit"
              data-testid="booking-submit-button"
              disabled={sending}
              className="group flex items-center justify-center gap-3 bg-gold px-8 py-4 font-cond text-base font-bold uppercase tracking-[0.18em] text-black transition-all duration-300 hover:bg-gold-hover hover:shadow-[0_0_28px_rgba(255,85,0,0.45)] active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2"
            >
              <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              {sending ? "Sending…" : "Request Appointment"}
            </button>
          </form>
        </Reveal>

        <div id="contact" className="md:col-span-5">
          <Reveal delay={0.12}>
            <div
              data-testid="contact-info-section"
              className="relative border border-line bg-panel p-8 md:p-10"
              style={{ boxShadow: "0 0 80px rgba(255,85,0,0.08)" }}
            >
              <span className="absolute -left-px -top-px h-5 w-5 border-l-2 border-t-2 border-gold" />
              <span className="absolute -bottom-px -right-px h-5 w-5 border-b-2 border-r-2 border-gold" />
              <MonoLabel>Find The Shop</MonoLabel>

              <div className="mt-8 space-y-7">
                <div className="flex gap-4">
                  <MapPin size={18} className="mt-1 shrink-0 text-gold" strokeWidth={1.7} />
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.22em] text-zinc-300">
                      Visit
                    </p>
                    <p className="mt-2 font-cond text-lg font-semibold uppercase leading-snug tracking-[0.06em] text-zinc-100">
                      580 Jersey Ave, Unit 2
                      <br />
                      New Brunswick, NJ 08901
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Phone size={18} className="mt-1 shrink-0 text-gold" strokeWidth={1.7} />
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.22em] text-zinc-300">
                      Call
                    </p>
                    <a
                      href="tel:+17323392277"
                      data-testid="contact-phone-link"
                      className="mt-2 inline-block font-cond text-lg font-semibold tracking-[0.06em] text-zinc-100 underline decoration-gold/50 underline-offset-4 transition-colors hover:text-gold"
                    >
                      732-339-2277
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Mail size={18} className="mt-1 shrink-0 text-gold" strokeWidth={1.7} />
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.22em] text-zinc-300">
                      Email
                    </p>
                    <a
                      href="mailto:amos.olayemi@yahoo.com"
                      data-testid="contact-email-link"
                      className="mt-2 inline-block break-all font-cond text-lg font-semibold tracking-[0.06em] text-zinc-100 underline decoration-gold/50 underline-offset-4 transition-colors hover:text-gold"
                    >
                      amos.olayemi@yahoo.com
                    </a>
                  </div>
                </div>

                <div className="border-t border-line pt-7" data-testid="contact-hours-section">
                  <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-zinc-300">
                    <Clock size={12} className="text-gold" /> Opening Hours
                  </p>
                  <ul className="mt-4 space-y-1.5">
                    {HOURS.map(({ day, time }, i) => {
                      const isToday = i === todayIdx;
                      return (
                        <li
                          key={day}
                          className={`flex items-center justify-between font-mono text-xs uppercase tracking-[0.12em] ${isToday ? "text-gold" : "text-zinc-200"}`}
                        >
                          <span>
                            {day}
                            {isToday && <span className="text-gold"> · today</span>}
                          </span>
                          <span className={time === "Closed" ? "text-zinc-400" : ""}>
                            {time}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="border-t border-line pt-7">
                  <p className="font-mono text-xs uppercase tracking-[0.22em] text-zinc-300">
                    Requests
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-300">
                    Submit the service form and your request lands directly
                    with Amos — you'll get a personal confirmation before your
                    appointment.
                  </p>
                </div>

                <div
                  className="overflow-hidden border border-line"
                  data-testid="contact-map"
                >
                  <iframe
                    title="His Grace Auto Repair — 580 Jersey Ave, Unit 2, New Brunswick, NJ 08901"
                    src="https://www.google.com/maps?q=580%20Jersey%20Ave%2C%20New%20Brunswick%2C%20NJ%2008901&output=embed"
                    className="h-64 w-full"
                    style={{
                      border: 0,
                      filter: "grayscale(1) contrast(1.05) brightness(0.92)",
                    }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
